import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  Pagination,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  FaTrash,
  FaPlus,
} from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import * as api from '~/Services/Service';
import ModalIncluir from './components/ModalIncluir';

const PAGE_SIZE = 20;

const PortadorJuridico = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [filtroGE, setFiltroGE] = useState(null);
  const [dados, setDados] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [loading, setLoading] = useState(false);
  const [mostrarModalIncluir, setMostrarModalIncluir] = useState(false);
  const [itemExclusao, setItemExclusao] = useState(null);
  const requestSeq = useRef(0);

  const carregarGEs = useCallback(async () => {
    try {
      const resp = await api.listarGruposEmpresaPortadorJuridico();
      const opcoes = (resp || []).map(ge => ({
        value: ge.codigo_grupo_empresa,
        label: `${ge.codigo_grupo_empresa} - ${ge.descricao}`,
      }));
      setGruposEmpresa(opcoes);
      // RN-6: auto-selecionar quando o usuário tem apenas 1 GE
      if (opcoes.length === 1) {
        setFiltroGE(opcoes[0]);
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err) || 'Erro ao carregar grupos de empresa.');
    }
  }, []);

  const carregarDados = useCallback(async (codigoGE, paginaAtual) => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    try {
      const resp = await api.listarPortadoresJuridicos(
        codigoGE,
        paginaAtual,
        PAGE_SIZE
      );
      const payload = resp || {};
      if (requestSeq.current !== seq) return;
      setDados(payload.dados || []);
      setTotal(payload.total || 0);
    } catch (err) {
      if (requestSeq.current !== seq) return;
      toast.error(extrairMensagemErro(err) || 'Erro ao carregar vínculos.');
      setDados([]);
      setTotal(0);
    } finally {
      if (requestSeq.current === seq) setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarGEs();
  }, [carregarGEs]);

  useEffect(() => {
    const codigoGE = filtroGE ? filtroGE.value : null;
    carregarDados(codigoGE, pagina);
  }, [filtroGE, pagina, carregarDados]);

  const handleAlterarGE = opt => {
    setFiltroGE(opt);
    setPagina(1);
  };

  const handleLimparFiltro = () => {
    setFiltroGE(null);
    setPagina(1);
  };

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const mudarPagina = numero => {
    if (numero < 1 || numero > totalPaginas) return;
    setPagina(numero);
  };

  const handleIncluido = () => {
    setMostrarModalIncluir(false);
    toast.success('Vínculo incluído com sucesso.');
    // Edge case LICOES §1.4: navegar para a última página onde o item aparece após ordenação padrão
    const codigoGE = filtroGE ? filtroGE.value : null;
    const novaUltimaPagina = Math.max(
      1,
      Math.ceil((total + 1) / PAGE_SIZE)
    );
    if (pagina !== novaUltimaPagina) {
      setPagina(novaUltimaPagina);
    } else {
      carregarDados(codigoGE, novaUltimaPagina);
    }
  };

  const handleConfirmarExclusao = async confirmado => {
    const alvo = itemExclusao;
    setItemExclusao(null);
    if (!confirmado || !alvo) return;
    try {
      await api.excluirPortadorJuridico({
        codigo_grupo_empresa: alvo.codigo_grupo_empresa,
        codigo_portador: alvo.codigo_portador,
        codigo_portador_juridico: alvo.codigo_portador_juridico,
      });
      toast.success('Vínculo excluído com sucesso.');
      // Edge case LICOES §1.4: último item da última página → voltar uma página
      const codigoGE = filtroGE ? filtroGE.value : null;
      if (dados.length === 1 && pagina === totalPaginas && pagina > 1) {
        setPagina(pagina - 1);
      } else {
        carregarDados(codigoGE, pagina);
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err) || 'Erro ao excluir vínculo.');
    }
  };

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const itens = [];
    const maxBotoes = 5;
    itens.push(
      <Pagination.First
        key="first"
        onClick={() => mudarPagina(1)}
        disabled={pagina === 1}
      />
    );
    itens.push(
      <Pagination.Prev
        key="prev"
        onClick={() => mudarPagina(pagina - 1)}
        disabled={pagina === 1}
      />
    );

    let inicio = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
    if (fim - inicio < maxBotoes - 1) {
      inicio = Math.max(1, fim - maxBotoes + 1);
    }
    if (inicio > 1) {
      itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }
    for (let n = inicio; n <= fim; n += 1) {
      itens.push(
        <Pagination.Item
          key={`page-${n}`}
          active={n === pagina}
          onClick={() => mudarPagina(n)}
        >
          {n}
        </Pagination.Item>
      );
    }
    if (fim < totalPaginas) {
      itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }
    itens.push(
      <Pagination.Next
        key="next"
        onClick={() => mudarPagina(pagina + 1)}
        disabled={pagina === totalPaginas}
      />
    );
    itens.push(
      <Pagination.Last
        key="last"
        onClick={() => mudarPagina(totalPaginas)}
        disabled={pagina === totalPaginas}
      />
    );

    const inicioRegistro = total === 0 ? 0 : (pagina - 1) * PAGE_SIZE + 1;
    const fimRegistro = Math.min(pagina * PAGE_SIZE, total);

    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap mt-3 mb-2 px-3">
        <div className="text-muted small mr-2 mb-2">
          Mostrando {inicioRegistro} até {fimRegistro} de {total} registros
        </div>
        <Pagination className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  const thStyle = {
    whiteSpace: 'nowrap',
  };

  const mensagemConfirmacao = itemExclusao
    ? `Deseja remover o vínculo entre ${itemExclusao.apelido_portador} e ${itemExclusao.apelido_portador_juridico} para o GE ${itemExclusao.descricao_ge}?`
    : '';

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Portador Jurídico" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-center">
            <Col xs={12} md={6} lg={5} className="mb-2 mb-md-0">
              <Select
                placeholder="Filtrar por grupo de empresa..."
                options={gruposEmpresa}
                value={filtroGE}
                onChange={handleAlterarGE}
                isClearable
                components={{ DropdownIndicator }}
                styles={customStyles}
                noOptionsMessage={() => 'Nenhum grupo de empresa disponível'}
              />
            </Col>
            <Col
              xs={12}
              md={6}
              lg={7}
              className="d-flex justify-content-md-end flex-wrap"
            >
              <Button
                size="sm"
                variant="secondary"
                className="mr-2 mb-2 mb-md-0"
                onClick={handleLimparFiltro}
                disabled={!filtroGE}
              >
                Limpar
              </Button>
              <Button
                size="sm"
                variant="success"
                onClick={() => setMostrarModalIncluir(true)}
              >
                <FaPlus className="mr-1" /> Incluir
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Vínculos Portador × Portador Jurídico
            <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              ({total} {total === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {loading && (
            <div className="p-3 text-center">
              <Loading />
            </div>
          )}

          {!loading && dados.length === 0 && (
            <p className="text-muted text-center my-4 px-3">
              Nenhum vínculo encontrado.
            </p>
          )}

          {!loading && dados.length > 0 && (
            <>
              <div className="d-none d-md-block">
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: '80px' }}>
                        GE
                      </th>
                      <th style={thStyle}>
                        Empreendimento
                      </th>
                      <th style={{ ...thStyle, width: '100px' }}>
                        Portador
                      </th>
                      <th style={thStyle}>
                        Descrição Portador
                      </th>
                      <th style={{ ...thStyle, width: '110px' }}>
                        Port. Jurídico
                      </th>
                      <th style={thStyle}>
                        Descrição Portador Jurídico
                      </th>
                      <th
                        style={{ width: '80px', textAlign: 'center' }}
                      >
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.map(item => (
                      <tr
                        key={`${item.codigo_grupo_empresa}-${item.codigo_portador}-${item.codigo_portador_juridico}`}
                      >
                        <td>{item.codigo_grupo_empresa}</td>
                        <td>{item.descricao_ge}</td>
                        <td>{item.codigo_portador}</td>
                        <td>{item.apelido_portador}</td>
                        <td>{item.codigo_portador_juridico}</td>
                        <td>{item.apelido_portador_juridico}</td>
                        <td className="text-center">
                          <Button
                            size="sm"
                            variant="outline-danger"
                            title="Excluir vínculo"
                            onClick={() => setItemExclusao(item)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="d-md-none p-2">
                {dados.map(item => (
                  <Card
                    key={`mob-${item.codigo_grupo_empresa}-${item.codigo_portador}-${item.codigo_portador_juridico}`}
                    className="mb-2 shadow-sm"
                  >
                    <Card.Body className="py-2 px-3">
                      <div className="mb-1">
                        <small className="text-muted">GE:</small>{' '}
                        <strong>
                          {item.codigo_grupo_empresa} — {item.descricao_ge}
                        </strong>
                      </div>
                      <div className="mb-1" style={{ fontSize: '0.9rem' }}>
                        <small className="text-muted">Portador:</small>{' '}
                        {item.codigo_portador} — {item.apelido_portador}
                      </div>
                      <div className="mb-2" style={{ fontSize: '0.9rem' }}>
                        <small className="text-muted">Portador Jurídico:</small>{' '}
                        {item.codigo_portador_juridico} —{' '}
                        {item.apelido_portador_juridico}
                      </div>
                      <div className="text-right">
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setItemExclusao(item)}
                        >
                          <FaTrash className="mr-1" /> Excluir
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </>
          )}

          {!loading && renderizarPaginacao()}
        </Card.Body>
      </Card>

      <ModalConfirmacao
        show={!!itemExclusao}
        title="Confirmar Exclusão"
        message={mensagemConfirmacao}
        onClose={handleConfirmarExclusao}
      />

      <ModalIncluir
        mostrar={mostrarModalIncluir}
        onFechar={() => setMostrarModalIncluir(false)}
        onSalvar={handleIncluido}
        gruposEmpresa={gruposEmpresa}
      />
    </Container>
  );
};

export default PortadorJuridico;
